import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { BrandWord } from "@/components/BrandWord";
import { passwordProblem } from "@/lib/passwordPolicy";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Missing reset token"); return; }
    const pwErr = passwordProblem(pwd);
    if (pwErr) { toast.error(pwErr); return; }
    if (pwd !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col">
      <div className="max-w-[1400px] w-full mx-auto px-4 sm:px-8 lg:px-12 py-8 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/logo192.png" alt="ShiftQuote" className="w-8 h-8 object-contain shrink-0" />
          <BrandWord className="font-heading text-lg font-black tracking-tight" />
        </Link>
        <Link to="/login" className="btn-ghost flex items-center gap-2" data-testid="reset-back-link">
          <ArrowLeft className="w-4 h-4" /> Sign in
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md border border-white/10 bg-[#121214] p-8 sm:p-10">
          {!done ? (
            <>
              <div className="label-eyebrow mb-4">SET NEW PASSWORD</div>
              <h1 className="font-heading text-3xl font-black tracking-tighter mb-3">Reset password</h1>
              <p className="text-sm text-zinc-400 mb-8">Choose a strong password you haven't used before.</p>

              {!token && (
                <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-4 text-sm text-zinc-300 mb-6" data-testid="reset-no-token">
                  Reset token missing. Request a new link from the <Link to="/forgot-password" className="text-white underline">forgot password</Link> page.
                </div>
              )}

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="label-eyebrow block mb-2">NEW PASSWORD</label>
                  <input required type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} className="input-base" data-testid="reset-new-password" />
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">CONFIRM PASSWORD</label>
                  <input required type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="input-base" data-testid="reset-confirm-password" />
                </div>
                <button type="submit" disabled={loading || !token} className="btn-primary w-full" data-testid="reset-submit-btn">
                  {loading ? "..." : "Update password"}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-6" data-testid="reset-success">
              <div className="w-12 h-12 mx-auto bg-[#34C759] flex items-center justify-center mb-5">
                <CheckCircle2 className="w-6 h-6 text-black" strokeWidth={2.5} />
              </div>
              <h1 className="font-heading text-2xl font-black tracking-tighter mb-3">Password updated</h1>
              <p className="text-sm text-zinc-400 mb-6">Redirecting to sign in...</p>
              <Link to="/login" className="btn-primary inline-block">Sign in now</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
